import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useContent } from '@/hooks/useContent';
import { interpolateContent } from '@/lib/content';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { QuickTutorialModal } from '@/components/common/QuickTutorialModal';
import { authUserMetaAtom } from '@/store/authAtoms';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { tutorialOpenAtom } from '@/store/tutorialAtom';
import { useLegalLinks } from '@/hooks/useLegalLinks';
import { LegalFooterLinks } from '@/components/common/LegalFooterLinks';
import { patientProfileAtom } from '@/store/patientProfileAtom';
import { authService } from '@/services/authService';
import { patientService } from '@/services/patientService';
import { messageService, messageKeys } from '@/services/messageService';
import { ToastContainer, useToast } from '@/components/common/Toast';
import { AriaFab } from '@/components/common/AriaFab';
import { ParagraphCodeModeBadge } from '@/components/common/ParagraphCodeModeBadge';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import NavAccountLogin from '@/components/common/NavAccountLogin';
import NavCreateAccount from '@/components/common/NavCreateAccount';
import { navAccountLoginActiveAtom } from '@/store/navAccountLoginAtom';
import { IdleTimeoutDialog, AbsoluteTimeoutDialog } from '@/components/common/SessionTimeoutModal';
import { useTranslation } from 'react-i18next';
import { useSessionCaps } from '@/hooks/useSessionCaps';
import { useConfig, isYes } from '@/hooks/useConfig';
import { useLiteMode } from '@/hooks/useLiteMode';
import { useLongPress } from '@/hooks/useLongPress';
import { useScreenSession } from '@/hooks/useScreenSession';
import { VersionInfoPopover } from '@/components/common/VersionInfoPopover';
import Alert from '@mui/material/Alert';
import { viewingAsAtom, homeUserMetaAtom, delegateAccessEndReasonAtom, delegateAccessFailureNoticeAtom } from '@/store/authAtoms';
import { medicalRecordAccessService, type AccessiblePatient } from '@/services/medicalRecordAccessService';

const SIDEBAR_EXPANDED_W = 256;
const SIDEBAR_COLLAPSED_W = 64;

/* ── Authorized-access delegate context banner ── */
const viewedPatientClinicalQueryKeys = [
  'patientAttachments',
  'patientAttachmentPage',
  'pacsStudy',
  'dicomStatus',
  'dicomMediaActive',
  'dicomMediaHistory',
  'gfeDocument',
  'exam-activity-log',
] as const;

/** Drop clinical caches that are not keyed by the acting user before the next identity's reads. */
function clearViewedPatientClinicalCache(queryClient: QueryClient) {
  for (const key of viewedPatientClinicalQueryKeys) {
    queryClient.removeQueries({ queryKey: [key] });
  }
  void queryClient.invalidateQueries({ queryKey: ['exams'] });
  void queryClient.invalidateQueries({ queryKey: ['appointments'] });
}

function AuthorizedAccessBanner() {
  const { getContent, getContentString } = useContent();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [viewingAs, setViewingAs] = useAtom(viewingAsAtom);
  const [user, setMeta] = useAtom(authUserMetaAtom);
  const [homeUserMeta, setHomeUserMeta] = useAtom(homeUserMetaAtom);
  const setProfile = useSetAtom(patientProfileAtom);
  const [switching, setSwitching] = useState(false);

  const accessibleKey = ['authorizedAccessiblePatients', user?.id] as const;

  // Only fetch "which patients can I view" with the delegate's OWN identity — while
  // already viewing-as, authUserMetaAtom holds the target patient's context, which
  // would answer this question for the wrong person.
  const accessible = useQuery({
    queryKey: accessibleKey,
    queryFn: () => medicalRecordAccessService.getAccessiblePatients(),
    enabled: !!user?.id && !viewingAs,
  });

  const switchTo = async (patientKey: number, name: string) => {
    setSwitching(true);
    try {
      const result = await medicalRecordAccessService.activatePatientContext(patientKey, name);
      setHomeUserMeta(user);
      setMeta({ id: String(result.patientKey), fullName: result.userName, roles: user?.roles ?? ['patient'] });
      setViewingAs({ patientKey: result.patientKey, name: result.name || name });
      setProfile(null);
      clearViewedPatientClinicalCache(queryClient);
    } catch {
      // Most likely: the grant was revoked between page load and this click — exactly
      // the race the backend re-validates against server-side. Drop it from the banner
      // immediately (don't make the user wait for a full refetch to stop seeing a dead
      // entry), then reconcile with the server in the background.
      toast(getContentString('PPDelegateSwitchError'), 'error');
      queryClient.setQueryData<AccessiblePatient[]>(accessibleKey, (old) =>
        (old ?? []).filter((p) => p.patientKey !== patientKey),
      );
      void queryClient.invalidateQueries({ queryKey: accessibleKey });
    } finally {
      setSwitching(false);
    }
  };

  const switchBack = () => {
    void medicalRecordAccessService.switchBack().then(() => {
      localStorage.removeItem('delegate_token');
      if (homeUserMeta) setMeta(homeUserMeta);
      setHomeUserMeta(null);
      setViewingAs(null);
      setProfile(null);
      clearViewedPatientClinicalCache(queryClient);
    }).catch(() => {
      toast(getContentString('PPDelegateSwitchError'), 'error');
    });
  };

  if (viewingAs) {
    return (
      <Alert
        severity="warning"
        variant="filled"
        sx={{ borderRadius: 0, alignItems: 'center' }}
        action={
          <Button color="inherit" size="small" variant="outlined" onClick={switchBack}>
            {getContent('PPDelegateSwitchBack')}
          </Button>
        }
      >
        {getInterpolatedText(getContentString('PPDelegateViewingAs'), { name: viewingAs.name })}
      </Alert>
    );
  }

  if (!accessible.data || accessible.data.length === 0) return null;

  return (
    <Alert severity="info" variant="filled" sx={{ borderRadius: 0, alignItems: 'center' }}>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          {getInterpolatedText(getContentString('PPDelegateAccessibleCount'), { count: accessible.data.length })}
        </Typography>
        {accessible.data.map((p) => (
          <Button
            key={p.patientKey}
            color="inherit"
            variant="outlined"
            size="small"
            disabled={switching}
            onClick={() => void switchTo(p.patientKey, `${p.firstName} ${p.lastName}`.trim())}
          >
            {p.firstName} {p.lastName}
          </Button>
        ))}
      </Stack>
    </Alert>
  );
}

function getInterpolatedText(template: string, tokens: Record<string, string | number>): string {
  return Object.entries(tokens).reduce(
    (text, [key, value]) => text.replaceAll(`{{${key}}}`, String(value)),
    template,
  );
}

/* ── Logo / Wordmark ── */
function DHWordmark({ compact = false }: Readonly<{ compact?: boolean }>) {
  // Per-facility branding overrides (set via pp_configuration_override).
  // Falls back to DeepHealth defaults when nothing is configured.
  // All useConfig calls are unconditional — hooks must not be called inside ?? short-circuit chains.
  const portalLogoUrl      = useConfig('PortalLogo');
  const patientPortalName  = useConfig('PatientPortalName');
  const portalNameConfig   = useConfig('PortalName');
  const portalShortName    = useConfig('PortalShortName');
  const { getContent }     = useContent();

  const portalName  = patientPortalName  ?? portalNameConfig ?? 'Patient Portal';
  const portalShort = portalShortName    ?? portalNameConfig ?? getContent('PPPortalDefaultName');

  const titleRef = useRef<HTMLDivElement>(null);
  const [versionAnchor, setVersionAnchor] = useState<HTMLElement | null>(null);
  const longPressHandlers = useLongPress(() => {
    if (titleRef.current) setVersionAnchor(titleRef.current);
  });

  return (
    <>
      <VersionInfoPopover
        anchorEl={versionAnchor}
        onClose={() => setVersionAnchor(null)}
        showReset={false}
      />
    <Stack ref={titleRef} direction="row" spacing={compact ? 0 : 1.25} {...longPressHandlers} sx={{ alignItems: 'center', justifyContent: compact ? 'center' : 'flex-start', flexShrink: 0, cursor: 'default', userSelect: 'none' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          // Use the theme primary gradient so the badge picks up the practice's
          // chosen palette automatically, instead of the hardcoded purple.
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: 2,
          overflow: 'hidden',
        }}
      >
        {portalLogoUrl ? (
          <Box component="img" src={portalLogoUrl} alt="" aria-hidden sx={{ width: 28, height: 28, objectFit: 'contain' }} />
        ) : (
          <svg viewBox="0 0 24 24" fill="white" width={18} height={18} aria-hidden="true" focusable="false">
            <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
          </svg>
        )}
      </Box>
      {!compact && (
        <Box sx={{ overflow: 'hidden', display: { xs: 'none', sm: 'block' } }}>
          <Typography variant="body2" color="text.primary" noWrap  sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {portalName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1 }}>
            {portalShort}
          </Typography>
        </Box>
      )}
    </Stack>
    </>
  );
}

/* ── Main Layout ── */
export default function AppLayout() {
  const [user, setMeta] = useAtom(authUserMetaAtom);
  const remoteAccessCtx = useAtomValue(remoteAccessContextAtom);
  const isRemoteAccess = remoteAccessCtx !== null;
  const isNoAccountRemoteAccess = remoteAccessCtx?.isNoAccount === true;
  const [profile, setProfile] = useAtom(patientProfileAtom);
  const viewingAs = useAtomValue(viewingAsAtom);
  const delegateAccessFailureNotice = useAtomValue(delegateAccessFailureNoticeAtom);
  const delegateAccessEndReason = useAtomValue(delegateAccessEndReasonAtom);
  const lastDelegateAccessFailureNotice = useRef(0);
  const setViewingAs = useSetAtom(viewingAsAtom);
  const setHomeUserMeta = useSetAtom(homeUserMetaAtom);
  const location = useLocation();
  const { t } = useTranslation();
  const { getContent, getContentString } = useContent();
  const { toast } = useToast();
  const muiTheme = useTheme();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [tutorialOpen, setTutorialOpen]   = useAtom(tutorialOpenAtom);
  const { canAccess, isGuestSession, isICodeSession } = useSessionCaps();

  const [routeAnnouncement, setRouteAnnouncement] = useState('');
  const prevPathRef = useRef(location.pathname);

  const { links: legalLinks } = useLegalLinks();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem('sidebar-collapsed');
    return stored === null ? true : stored === 'true';
  });
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const navAccountLoginActive = useAtomValue(navAccountLoginActiveAtom);

  // Per-facility feature gates — must be read before the inbox query so we can
  // skip a known-disabled round-trip. Stacked with canAccess() further below.
  //
  // AppConfig keys (pp_configuration_override → appsettings.json → default):
  const healthSummaryEnabled = isYes(useConfig('PPHealthSummaryEnabled'));
  const helpEnabled          = isYes(useConfig('PPGetHelpAccessEnabled'));
  const messagingEnabled     = isYes(useConfig('PPMessagesAccessEnabled'));
  const lite = useLiteMode();
  // (no SystemConfig keys read here today — add `useSystemConfig(...)` if needed)

  // Shares the cache key with Dashboard's inbox query, so both views show
  // the same unread count without a second network call.
  const { data: inboxPage } = useQuery({
    queryKey: messageKeys.inbox(1, user?.id),
    queryFn: () => messageService.getInbox(1),
    // Restricted sessions (iCode/guest) have no c_user row — /Messages/Inbox requires a
    // real user_key and 401s for them. That 401 trips the axios interceptor's global
    // auth-clear-and-redirect, which is why an iCode session got bounced off any route
    // rendered inside AppLayout, not just /messages itself. Remote-access (admin spoof)
    // sessions are excluded too since they don't have their own inbox to fetch.
    // Skip entirely when messaging is feature-flagged off for this facility.
    enabled: !!user?.id && !isGuestSession && !isICodeSession && !isRemoteAccess && messagingEnabled && lite.isSectionVisible('messages'),
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  // NOTE: this shared patientProfileAtom is only loaded for the normal full-session patient.
  // While viewing-as, /Patient/* is intentionally outside the delegate-token controller
  // allowlist, so fetching the target patient's profile here would correctly 403 and add
  // noisy API errors. Account.tsx has its own accountIdentityAtom-scoped
  // profile query for the logged-in user's own identity.
  const { data: fetchedProfile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: () => patientService.getProfile(Number(user!.id)),
    // Restricted iCode sessions (e.g. GFE) don't need profile data and may not carry a
    // renewable access token — skip the fetch the same way guest sessions are skipped.
    enabled: !!user?.id && !viewingAs && !isGuestSession && !isICodeSession,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (fetchedProfile) setProfile(fetchedProfile);
  }, [fetchedProfile, setProfile]);

  const unreadMessages =
    inboxPage?.messagingEnabled === false
      ? 0
      : (inboxPage?.items ?? []).filter((item) => !item.readFlag).length;

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  }, []);

  const handleLogout = useCallback(() => {
    void authService.logout().catch(() => undefined);
    setMeta(null);
    setProfile(null);
    // Viewing metadata is persisted — without this, a delegate session's banner
    // would survive into the next login on the same browser.
    setViewingAs(null);
    setHomeUserMeta(null);
    localStorage.removeItem('delegate_token');
  }, [setMeta, setProfile, setViewingAs, setHomeUserMeta]);

  useEffect(() => {
    if (delegateAccessFailureNotice <= lastDelegateAccessFailureNotice.current) return;
    lastDelegateAccessFailureNotice.current = delegateAccessFailureNotice;
    const code = delegateAccessEndReason === 'revoked' ? 'PPDelegateAccessRevoked' : 'PPDelegateAccessExpired';
    toast(getContentString(code), 'error');
  }, [delegateAccessEndReason, delegateAccessFailureNotice, getContentString, toast]);

  const { phase, countdown, extend, logout: sessionLogout } = useScreenSession();

  const tabs = [
    { to: '/dashboard',      contentKey: 'PPNavDashboard',     fallback: 'nav.dashboard',     icon: <DashboardOutlinedIcon fontSize="small" />, badge: 0, show: lite.isSectionVisible('dashboard') },
    { to: '/exams',          contentKey: 'PPNavExams',         fallback: 'nav.exams',         icon: <AssignmentOutlinedIcon fontSize="small" />, badge: 0, show: lite.isSectionVisible('exams') },
    { to: '/appointments',   contentKey: 'PPNavAppointments',  fallback: 'nav.appointments',  icon: <CalendarMonthOutlinedIcon fontSize="small" />, badge: 0, show: lite.isSectionVisible('appointments') },
    { to: '/health-summary', contentKey: 'PPNavHealthSummary', fallback: 'nav.healthSummary', icon: <FavoriteBorderIcon fontSize="small" />,    badge: 0, show: healthSummaryEnabled && lite.isSectionVisible('healthSummary') },
    { to: '/messages',       contentKey: 'PPNavMessages',      fallback: 'nav.messages',      icon: <ChatBubbleOutlineIcon fontSize="small" />, badge: unreadMessages, show: messagingEnabled && !isNoAccountRemoteAccess && lite.isSectionVisible('messages') },
    { to: '/share',          contentKey: 'PPNavShare',         fallback: 'nav.share',         icon: <ShareOutlinedIcon fontSize="small" />,     badge: 0, show: false },
    { to: '/help',           contentKey: 'PPNavHelp',          fallback: 'nav.help',          icon: <HelpOutlineIcon fontSize="small" />,       badge: 0, show: helpEnabled && lite.isSectionVisible('help') },
  ].filter((tab) => tab.show && canAccess(tab.to));

  function resolvePageName(pathname: string, tabDefs: { to: string; contentKey: string; fallback: string }[]): string {
    const matched = tabDefs.find(tab => pathname.startsWith(tab.to));
    return matched ? (getContentString(matched.contentKey) || t(matched.fallback)) : 'Patient Portal';
  }

  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;
    const allTabDefs = [...tabs, { to: '/account', contentKey: 'PPNavAccount', fallback: 'nav.account' }];
    const pageName = resolvePageName(location.pathname, allTabDefs);
    document.title = `${pageName} — Patient Portal`;
    setRouteAnnouncement(interpolateContent(getContentString('PPNavNavigatedTo') || t('nav.navigatedTo'), { page: pageName }));
  });

  // Hide the tab for the page you're already on — keep it elsewhere as a one-tap jump.
  const onDashboard = location.pathname.startsWith('/dashboard');
  const onExams = location.pathname.startsWith('/exams');
  const onAppointments = location.pathname.startsWith('/appointments');
  const onHealthSummary = location.pathname.startsWith('/health-summary');
  const onMessages = location.pathname.startsWith('/messages');
  const mobileBottomTabs = [
    { to: '/dashboard',      contentKey: 'PPNavDashboard',     fallback: 'nav.dashboard',     icon: <DashboardOutlinedIcon />,     badge: 0, show: lite.isSectionVisible('dashboard') && !onDashboard },
    { to: '/exams',          contentKey: 'PPNavExams',         fallback: 'nav.exams',         icon: <AssignmentOutlinedIcon />,    badge: 0, show: lite.isSectionVisible('exams') && !onExams },
    { to: '/appointments',   contentKey: 'PPNavAppointments',  fallback: 'nav.appointments',  icon: <CalendarMonthOutlinedIcon />, badge: 0, show: lite.isSectionVisible('appointments') && !onAppointments },
    { to: '/health-summary', contentKey: 'PPNavHealthSummary', fallback: 'nav.healthSummary', icon: <FavoriteBorderIcon />,        badge: 0, show: healthSummaryEnabled && lite.isSectionVisible('healthSummary') && !onHealthSummary },
    { to: '/messages',       contentKey: 'PPNavMessages',      fallback: 'nav.messages',      icon: <ChatBubbleOutlineIcon />,     badge: unreadMessages, show: messagingEnabled && !isNoAccountRemoteAccess && lite.isSectionVisible('messages') && !onMessages },
    { to: '/account',        contentKey: 'PPNavAccount',       fallback: 'nav.account',       icon: <PersonOutlineIcon />,         badge: 0, show: true },
  ].filter((tab) => tab.show && canAccess(tab.to));

  const visibleBottomTabs = mobileBottomTabs;

  // Resizable sidebar
  const [sidebarDragWidth, setSidebarDragWidth] = useState<number | null>(null);
  const sidebarDragging = useRef(false);

  const onSidebarDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    sidebarDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: PointerEvent) => {
      if (!sidebarDragging.current) return;
      const newW = Math.max(SIDEBAR_COLLAPSED_W, Math.min(ev.clientX, 400));
      setSidebarDragWidth(newW);
    };
    const onUp = () => {
      sidebarDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);

      setSidebarDragWidth((w) => {
        if (w !== null && w <= SIDEBAR_COLLAPSED_W + 20) {
          if (!collapsed) toggleCollapsed();
          return null;
        }
        if (w !== null && collapsed) toggleCollapsed();
        return w;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [collapsed, toggleCollapsed]);

  // Hover-expand: when collapsed, hovering temporarily expands the rail without
  // flipping the persisted preference. Dragging takes precedence (sidebarDragWidth).
  // Keep the rail expanded while the nav login form has focus — otherwise mouse-leave
  // re-collapses it and unmounts NavAccountLogin, wiping out whatever was typed.
  const visuallyCollapsed = collapsed && !sidebarHovered && !navAccountLoginActive;
  const sidebarWidth = sidebarDragWidth ?? (visuallyCollapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W);

  const bottomNavValue = visibleBottomTabs.findIndex(tab => location.pathname.startsWith(tab.to));

  // Top header actions
  const showHeaderBell = tabs.some((tab) => tab.to === '/messages');
  const headerAccountLabel = getContentString('PPNavAccount') || t('nav.account');
  const headerLogoutLabel = (isGuestSession || isICodeSession)
    ? (getContentString('PPGuestExit') || 'Exit')
    : (getContentString('PortalLogout') || t('nav.logout'));
  // On xs the label is hidden (icon-only, 44px target); from sm up it is a labelled outlined button.
  const headerButtonSx = {
    minHeight: 40,
    minWidth: { xs: 44, sm: 64 },
    px: { xs: 1, sm: 2 },
    textTransform: 'none',
    flexShrink: 0,
    '& .MuiButton-startIcon': {
      marginInlineStart: { xs: 0, sm: -0.5 },
      marginInlineEnd: { xs: 0, sm: 1 },
    },
  } as const;

  /* ── Sidebar content (shared between permanent and mobile drawer) ── */
  const sidebarContent = (isMobile: boolean) => {
    const effectiveCollapsed = isMobile ? false : visuallyCollapsed;
    const effectiveWidth = isMobile ? 280 : sidebarWidth;

    // Collapsed rail geometry. Expanded, a nav control is a full-width row and its selected
    // state reads as a pill — correct there. Collapsed, that same rule stretched the background
    // across the whole 48px content box, so the selection looked like a clipped pill crammed
    // into the rail. Here each control becomes a fixed square instead: the selection SHAPE is
    // kept (a rounded square) but it now sits inside the narrow area rather than filling it.
    //
    // 44px is deliberate — it clears the 24px minimum target size (WCAG 2.2 SC 2.5.8) with room
    // to spare, and centres exactly in the 64px rail once the List's 8px padding is accounted
    // for (8 + 2 + 44 + 2 + 8 = 64).
    const RAIL_ITEM = 44;
    const railItem = effectiveCollapsed
      ? {
          width: RAIL_ITEM,
          height: RAIL_ITEM,
          minWidth: RAIL_ITEM,
          mx: 'auto',
          p: 0,
          justifyContent: 'center',
          // Tighter than the expanded radius: a 12px radius on a 44px box reads as a lozenge.
          borderRadius: 2,
        }
      : { px: 1.5, py: 1.2, justifyContent: 'flex-start', borderRadius: 3 };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: effectiveWidth, overflow: 'hidden' }}>
        {/* Logo — desktop only; mobile drawer renders its own wordmark in the close-button header */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 2.25, px: effectiveCollapsed ? 1.75 : 2.5, flexShrink: 0, justifyContent: effectiveCollapsed ? 'center' : 'flex-start' }}>
            <DHWordmark compact={effectiveCollapsed} />
          </Box>
        )}

        {/* Brand gradient separator */}
        <Box
          aria-hidden
          sx={(theme) => ({
            mx: 1.5,
            mt: isMobile ? 1.5 : 0,
            mb: 1.5,
            height: '1px',
            borderRadius: '99px',
            flexShrink: 0,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
            opacity: 0.25,
          })}
        />

        {/* Navigation */}
        {/* minHeight: 0 is load-bearing. This List is a flex child of a fixed-height (100vh)
            column, and a flex item's default `min-height: auto` refuses to shrink below its
            content. With `overflow: hidden` that combination silently CLIPPED the tail of the
            nav — Messages and Get Help disappeared with no scrollbar once the sidebar's
            fixed chrome (logo + bottom language/collapse block) left the list less
            room than its rows needed. That bit expanded harder than collapsed (the expanded
            bottom block is taller) and got worse as browser zoom shrank the viewport. Keep
            overflowX hidden — the width transition would otherwise flash a horizontal bar. */}
        <List component="nav" aria-label="Primary navigation" sx={{ flex: 1, minHeight: 0, px: 1, py: 0.5, overflowX: 'hidden', overflowY: 'auto' }}>
          {tabs.map((tab) => {
            const isActive = location.pathname.startsWith(tab.to);
            const label = getContent(tab.contentKey) || t(tab.fallback);
            const navItem = (
              <ListItemButton
                key={tab.to}
                component={NavLink}
                to={tab.to}
                onClick={isMobile ? () => setMobileOpen(false) : undefined}
                sx={(theme) => ({
                  mb: 0.25,
                  ...railItem,
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  color: isActive ? theme.palette.primary.main : 'text.secondary',
                  '&:hover': {
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'action.hover',
                    color: isActive ? theme.palette.primary.main : 'text.primary',
                  },
                })}
              >
                <ListItemIcon sx={{ minWidth: effectiveCollapsed ? 0 : 36, color: 'inherit', justifyContent: 'center' }}>
                  {tab.badge > 0 ? (
                    <Badge badgeContent={tab.badge} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}>
                      {tab.icon}
                    </Badge>
                  ) : tab.icon}
                </ListItemIcon>
                {!effectiveCollapsed && (
                  <ListItemText
                    primary={label}
                    slotProps={{ primary: { style: { fontSize: '0.875rem', fontWeight: isActive ? 600 : 500 }, noWrap: true } }}
                  />
                )}
              </ListItemButton>
            );

            return effectiveCollapsed ? (
              <Tooltip key={tab.to} title={label} placement="right" arrow>
                {navItem}
              </Tooltip>
            ) : navItem;
          })}
        </List>

        {/* Bottom: language + restricted-session hooks + collapse */}
        <Box sx={{ py: 1.5, borderTop: 1, borderColor: 'divider', mt: 1, flexShrink: 0, px: effectiveCollapsed ? 1 : 1.5 }}>
          {/* Nav account hooks for restricted (iCode / guest) sessions:
              • patient already has a portal account → sign-in form to trade up to a full session
              • patient has no account yet        → "Get Started" create-account hook */}
          {!effectiveCollapsed && (
            (user?.session?.type === 'icode' && user.session.hasAccount) ||
            (user?.session?.type === 'guest' && user.session.hasAccount)
          ) && (
            <NavAccountLogin />
          )}

          {!effectiveCollapsed && (
            (user?.session?.type === 'icode' && !user.session.hasAccount) ||
            (user?.session?.type === 'guest' && user.session.canCreateAccount)
          ) && (
            <NavCreateAccount />
          )}

          {/* Language switcher */}
          {!effectiveCollapsed && (
            <Box sx={{ px: 0.5, mt: 0.5 }}>
              <LanguageSwitcher />
            </Box>
          )}

          {/* Legal links — mobile drawer only */}
          {isMobile && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 0.5, pb: 0.5, color: 'text.secondary' }}>
                <LegalFooterLinks
                  links={[legalLinks.privacyPolicy, legalLinks.termsOfService, legalLinks.hipaaNotice]}
                  variant="caption"
                  spacing={1}
                />
              </Box>
            </>
          )}

          {/* Collapse toggle (desktop only) */}
          {!isMobile && (
            <>
              {!effectiveCollapsed && <Divider sx={{ my: 0.5 }} />}
              <Tooltip title={effectiveCollapsed ? (getContent('PPNavExpandSidebar') || t('nav.expandSidebar')) : ''} placement="right">
                <ListItemButton
                  onClick={toggleCollapsed}
                  aria-label={collapsed ? (getContentString('PPNavExpandSidebar') || t('nav.expandSidebar')) : (getContentString('PPNavCollapseSidebar') || t('nav.collapseSidebar'))}
                  aria-expanded={!collapsed}
                  sx={{
                    mt: 0.5,
                    ...railItem,
                    color: 'text.disabled',
                    '&:hover': { bgcolor: 'action.hover', color: 'text.secondary' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: effectiveCollapsed ? 0 : 36, color: 'inherit', justifyContent: 'center' }}>
                    {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                  </ListItemIcon>
                  {!effectiveCollapsed && (
                    <ListItemText primary={collapsed ? (getContent('PPNavExpandSidebar') || t('nav.expandSidebar')) : (getContent('PPNavCollapseSidebar') || t('nav.collapseSidebar'))} slotProps={{ primary: { style: { fontSize: '0.75rem' } } }} />
                  )}
                </ListItemButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', maxHeight: '100vh', overflow: 'hidden', width: '100%', bgcolor: 'background.default' }}>
      {/* Skip nav */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          '&:focus': {
            width: 'auto',
            height: 'auto',
            clip: 'auto',
            overflow: 'visible',
            position: 'fixed',
            top: 8,
            insetInlineStart: 8,
            zIndex: 9999,
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderRadius: 2,
            boxShadow: 4,
            fontSize: '0.875rem',
            fontWeight: 500,
            border: 1,
            borderColor: 'divider',
          },
        }}
      >
        {getContent('PortalSkipToMain') || t('nav.skipToMain')}
      </Box>

      {/* Live region */}
      <Box component="div" role="status" aria-live="polite" aria-atomic="true" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {routeAnnouncement}
      </Box>

      {/* ══ DESKTOP SIDEBAR ══ */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          aria-label="Sidebar navigation"
          slotProps={{
            paper: {
              onMouseEnter: () => { if (collapsed) setSidebarHovered(true); },
              onMouseLeave: () => { if (sidebarHovered) setSidebarHovered(false); },
            },
          }}
          sx={{
            width: collapsed ? SIDEBAR_COLLAPSED_W : sidebarWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              // Edge border intentionally NOT set here — it lives in theme.config.ts
              // (MuiDrawer.paper.borderInlineEnd, driven by the `divider` token) so it follows
              // the active brand and can be overridden by a style pack or rule. This used to be
              // a hardcoded '1px solid #f0f0f0', which beat the theme and was wrong on any
              // non-light palette.
              transition: sidebarDragWidth ? 'none' : 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
              zIndex: (theme) => theme.zIndex.drawer + (sidebarHovered ? 1 : 0),
              boxShadow: sidebarHovered ? 4 : 'none',
            },
          }}
        >
          {sidebarContent(false)}

          {/* Sidebar drag handle */}
          <Box
            role="slider"
            tabIndex={0}
            aria-label="Resize sidebar"
            aria-valuenow={sidebarWidth}
            aria-valuemin={SIDEBAR_COLLAPSED_W}
            aria-valuemax={400}
            onPointerDown={onSidebarDragStart}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                setSidebarDragWidth((w) => Math.min((w ?? sidebarWidth) + 8, 400));
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setSidebarDragWidth((w) => Math.max((w ?? sidebarWidth) - 8, SIDEBAR_COLLAPSED_W));
              }
            }}
            sx={{
              position: 'absolute',
              insetInlineEnd: 0,
              top: 0,
              width: 4,
              height: '100%',
              cursor: 'col-resize',
              zIndex: 10,
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
              '&:hover > div': { bgcolor: 'primary.light' },
              '&:active > div': { bgcolor: 'primary.main' },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                insetInlineEnd: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 2,
                height: 32,
                borderRadius: 99,
                bgcolor: 'transparent',
                transition: 'background-color 150ms',
              }}
            />
          </Box>
        </Drawer>
      )}

      {/* ══ MOBILE DRAWER ══ */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        aria-label="Navigation menu"
        sx={{
          display: 'block',
          '@media (min-width: 1024px)': { display: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Close button header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, height: 56, borderBottom: 1, borderColor: 'divider' }}>
          <DHWordmark />
          <IconButton onClick={() => setMobileOpen(false)} aria-label={getContentString('PPNavCloseMenu') || t('nav.closeMenu')} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {sidebarContent(true)}
      </Drawer>

      {/* ══ CONTENT AREA ══ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {/* Top header (desktop + mobile): [menu] [brand] ... [bell] [account] [logout] */}
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          component="header"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, lg: 64 }, gap: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 2 } }}>
            {isDesktop ? (
              <IconButton
                onClick={toggleCollapsed}
                aria-label={collapsed
                  ? (getContentString('PPNavExpandSidebar') || t('nav.expandSidebar'))
                  : (getContentString('PPNavCollapseSidebar') || t('nav.collapseSidebar'))}
                aria-expanded={!collapsed}
                color="inherit"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label={getContentString('PPNavOpenMenu') || t('nav.openMenu')}
                aria-expanded={mobileOpen}
                aria-haspopup="dialog"
                color="inherit"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Desktop already shows the wordmark at the top of the sidebar */}
            {!isDesktop && <DHWordmark />}

            <Box sx={{ flexGrow: 1 }} />

            {showHeaderBell && (
              <IconButton
                component={Link}
                to="/messages"
                color="inherit"
                aria-label={`${getContentString('PPNavMessages') || t('nav.messages')}${unreadMessages > 0 ? ` (${unreadMessages})` : ''}`}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <Badge
                  badgeContent={unreadMessages}
                  color="info"
                  max={99}
                  sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}
                >
                  <NotificationsNoneOutlinedIcon />
                </Badge>
              </IconButton>
            )}

            {canAccess('/account') && (
              <Button
                component={Link}
                to="/account"
                variant="outlined"
                color="primary"
                startIcon={<PersonOutlineIcon />}
                aria-label={headerAccountLabel}
                sx={headerButtonSx}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{headerAccountLabel}</Box>
              </Button>
            )}

            <Button
              variant="outlined"
              color="primary"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              aria-label={headerLogoutLabel}
              sx={headerButtonSx}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{headerLogoutLabel}</Box>
            </Button>
          </Toolbar>
        </AppBar>

        <AuthorizedAccessBanner />

        {/* Page content */}
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            pb: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px))' },
            overflowX: 'hidden',
            overflowY: 'auto',
            '@media (min-width: 1024px)': { px: 3, py: 3, pb: 3 },
          }}
        >
          <Outlet />
        </Box>

        <ToastContainer />
        <AriaFab />
        <ParagraphCodeModeBadge />

        {/* Footer (desktop only) */}
        {isDesktop && (
          <Box
            component="footer"
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              px: { xs: 2, lg: 3 },
              py: 1.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {'© '}{(getContentString('PPFooterCopyrightPortal') || t('portal.copyright')).replace('{year}', String(new Date().getFullYear()))}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              {helpEnabled && lite.isSectionVisible('help') && canAccess('/help') && (
              <Button component={Link} to="/help" size="small" color="inherit" sx={{ fontSize: '0.75rem', minWidth: 0, '&:hover': { color: 'primary.main' } }}>
                {getContent('PPNavHelp') || t('nav.help')}
              </Button>
              )}
              <LegalFooterLinks
                links={[legalLinks.privacyPolicy, legalLinks.termsOfService, legalLinks.hipaaNotice]}
                variant="body2"
                spacing={2}
              />
            </Stack>
          </Box>
        )}
      </Box>

      {/* ══ MOBILE BOTTOM NAV ══ */}
      {!isDesktop && visibleBottomTabs.length > 0 && (
        <Paper
          component="nav"
          aria-label="Bottom navigation"
          sx={{
            position: 'fixed',
            bottom: 0,
            insetInline: 0,
            zIndex: 40,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(12px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          elevation={8}
        >
          <BottomNavigation
            value={bottomNavValue >= 0 ? bottomNavValue : false}
            showLabels
            sx={{ height: 'auto', minHeight: 60, bgcolor: 'transparent', px: 0.5, alignItems: 'stretch' }}
          >
            {visibleBottomTabs.map((tab) => (
              <BottomNavigationAction
                key={tab.to}
                component={NavLink}
                to={tab.to}
                label={getContent(tab.contentKey) || t(tab.fallback)}
                icon={
                  <Box className="bn-icon" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {tab.badge > 0 ? (
                      <Badge
                        badgeContent={tab.badge}
                        max={99}
                        color="error"
                        sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, px: 0.5, border: 2, borderColor: 'background.paper' } }}
                      >
                        {tab.icon}
                      </Badge>
                    ) : tab.icon}
                  </Box>
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: 'none',
                  minHeight: 56,
                  px: 0.25,
                  pt: 0.75,
                  pb: 0.5,
                  gap: 0.25,
                  position: 'relative',
                  color: 'text.secondary',
                  transition: 'color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .bn-icon': {
                    width: 48,
                    height: 28,
                    borderRadius: 1,
                    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiSvgIcon-root': { fontSize: 22 },
                  },
                  '& .MuiBottomNavigationAction-label': {
                    fontWeight: 400,
                    fontSize: '0.6875rem',
                    lineHeight: 1.2,
                    letterSpacing: 0,
                    textAlign: 'center',
                    maxWidth: '100%',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    overflowWrap: 'anywhere',
                    hyphens: 'auto',
                    transition: 'none',
                  },
                  '&.active, &.Mui-selected': {
                    color: 'primary.main',
                    '& .bn-icon': { bgcolor: (th) => alpha(th.palette.primary.main, 0.12) },
                    '& .MuiBottomNavigationAction-label': { fontWeight: 500, fontSize: '0.6875rem' },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      insetInline: '25%',
                      height: 3,
                      borderRadius: '0 0 4px 4px',
                      bgcolor: 'primary.main',
                    },
                  },
                  '&:not(.active):not(.Mui-selected):hover .bn-icon': { bgcolor: (th) => alpha(th.palette.primary.main, 0.06) },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}

      {/* Screen session — idle and absolute timeout dialogs */}
      <IdleTimeoutDialog
        open={phase === 'idle-warning'}
        countdown={countdown}
        onExtend={extend}
        onLogout={sessionLogout}
      />
      <AbsoluteTimeoutDialog
        open={phase === 'absolute-warning'}
        countdown={countdown}
        onLogout={sessionLogout}
      />
      <QuickTutorialModal open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </Box>
  );
}
